/**
 * Hook to manage product selection state
 */

import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProduct } from "@/features/product/product-context";
import { useProductOptions, type ProductWithMarketData } from "@/features/product/product-options";

const defaultProductId = "BTC-USD";

export const useProductSelection = (): {
  products: ProductWithMarketData[];
  selectedProduct: ProductWithMarketData | null | undefined;
  onChange: (product: ProductWithMarketData) => void;
  isLoading: boolean;
} => {
  const { product, setProduct } = useProduct();
  const { products, isLoading } = useProductOptions();

  const defaultProduct = useMemo(() => {
    if (!products) {
      return null;
    }

    const foundProduct = products.find((p) => p.id === defaultProductId);

    if (!foundProduct) {
      return null;
    }

    return foundProduct;
  }, [products]);

  const selectedProduct = useMemo(() => {
    if (!product) {
      return defaultProduct;
    }

    return product;
  }, [defaultProduct, product]);

  // On initial load, we won't have one set. So set it.
  useEffect(() => {
    if (product) {
      return;
    }

    if (defaultProduct) {
      setProduct(defaultProduct);
    }
  }, [defaultProduct, setProduct, product]);

  const navigate = useNavigate();

  const onChange = useCallback(
    (selectedProduct: ProductWithMarketData) => {
      setProduct(selectedProduct);
      // Navigate to new market URL (replace history to avoid back button cycling through markets)
      navigate(`/trade/${selectedProduct.id}`, { replace: true });
    },
    [setProduct, navigate]
  );

  return {
    products: products || [],
    selectedProduct,
    onChange,
    isLoading,
  };
};

